import React, { useRef, useEffect } from 'react';
import { Row, Col, Image } from "react-bootstrap";
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import { Trash2, Package, Zap, ArrowRight, Info, Home } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

const IMG_BANNER = "/images/bgparticuliers.jpg";
const IMG_INTRO = "/images/accompagnement.jpg";

const Particulier = () => {
  const services = [
    {
      icon: Trash2,
      title: "Évacuation d'ordures ménagères",
      text: "Gestion complète de vos déchets non dangereux avec tri et valorisation optimale.",
      color: "var(--color-primary)",
      image: "/images/menage.jpg"
    },
    {
      icon: Package,
      title: "Évacuation d'encombrants",
      text: "Enlèvement et valorisation de vos objets volumineux (meubles, électroménager, etc.).",
      color: "var(--color-accent)",
      image: "/images/encombrant.jpg"
    },
    {
      icon: Zap,
      title: "Déchets électriques et électroniques",
      text: "Collecte et recyclage responsable de vos DEEE (D3E) conformément à la réglementation.",
      color: "var(--color-secondary)",
      image: "/images/electrique.jpg"
    },
    {
      icon: Home,
      title: "Débarras",
      text: "Débarras d'appartement, box, cave, entrepôt, garage, grenier, maison.",
      color: "var(--color-primary)",
      image: "/images/debarras.jpg"
    }
  ];

  const introRef = useRef(null);
  const servicesRef = useRef(null);
  const calloutRef = useRef(null);
  const ctaRef = useRef(null);

  const introInView = useInView(introRef, { once: true, margin: "-80px" });
  const servicesInView = useInView(servicesRef, { once: true, margin: "-80px" });
  const calloutInView = useInView(calloutRef, { once: true, margin: "-80px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  const introControls = useAnimation();
  const servicesControls = useAnimation();
  const calloutControls = useAnimation();
  const ctaControls = useAnimation();

  useEffect(() => {
    if (introInView) introControls.start("visible");
  }, [introInView, introControls]);

  useEffect(() => {
    if (servicesInView) servicesControls.start("visible");
  }, [servicesInView, servicesControls]);

  useEffect(() => {
    if (calloutInView) calloutControls.start("visible");
  }, [calloutInView, calloutControls]);

  useEffect(() => {
    if (ctaInView) ctaControls.start("visible");
  }, [ctaInView, ctaControls]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* Hero : image en arrière-plan, texte par-dessus */}
      <div
        role="img"
        aria-label="Services particuliers - tri et valorisation"
        style={{
          position: 'relative',
          width: '100%',
          height: '50vh',
          minHeight: 280,
          backgroundImage: `url(${IMG_BANNER})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 0
          }}
        />
        <Container style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto', color: '#fff' }}
          >
            <motion.span
              style={{
                display: 'inline-block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                opacity: 0.95,
                marginBottom: 'var(--spacing-md)'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.95 }}
              transition={{ delay: 0.2 }}
            >
              Nos services
            </motion.span>
            <h1
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3rem)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: 'var(--spacing-lg)',
                lineHeight: 'var(--line-height-tight)',
                color: '#fff'
              }}
            >
              Services pour Particuliers
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                opacity: 0.95,
                lineHeight: 'var(--line-height-relaxed)',
                margin: 0,
                color: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              Gestion, tri et valorisation de vos déchets à domicile. Une solution simple et responsable.
            </p>
          </motion.div>
        </Container>
      </div>

      {/* Nos prestations */}
      <section className="section" style={{ backgroundColor: '#fff' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <SectionHeader
              title="Nos prestations"
              subtitle="Des solutions adaptées à vos besoins au quotidien"
            />
          </motion.div>
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesControls}
            variants={staggerContainer}
            style={{ margin: 'var(--spacing-3xl) auto 0' }}
          >
            <div className="grid-services-2x2">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                    className="card-modern"
                    style={{ height: '100%' }}
                  >
                    {service.image && (
                      <div
                        style={{
                          borderRadius: 'var(--border-radius-lg)',
                          overflow: 'hidden',
                          width: '100%',
                          height: 200,
                          marginBottom: 'var(--spacing-lg)'
                        }}
                      >
                        <img
                          src={service.image}
                          alt={service.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-md)',
                        marginBottom: 'var(--spacing-sm)'
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          background: service.color + '18',
                          color: service.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Icon size={22} />
                      </div>
                      <h3
                        style={{
                          fontSize: 'var(--font-size-lg)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--color-text)',
                          margin: 0
                        }}
                      >
                        {service.title}
                      </h3>
                    </div>
                    <p
                      style={{
                        fontSize: 'var(--font-size-base)',
                        color: 'var(--color-text-light)',
                        lineHeight: 'var(--line-height-relaxed)',
                        margin: 0
                      }}
                    >
                      {service.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Un accompagnement sur-mesure */}
      <section className="section" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Container>
          <motion.div
            ref={introRef}
            initial="hidden"
            animate={introControls}
            variants={fadeInUp}
          >
            <Row className="g-4 align-items-start">
              <Col md={4}>
                <div style={{ borderRadius: 'var(--border-radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-md)', height: 320, maxHeight: 320 }}>
                  <Image src={IMG_INTRO} alt="Accompagnement particuliers" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              </Col>
              <Col md={8}>
            <h3
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-lg)'
              }}
            >
              Un accompagnement sur-mesure
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                marginBottom: 'var(--spacing-2xl)'
              }}
            >
              Parce que nous sommes tous responsables des déchets que nous produisons, nous accompagnons
              les particuliers pour la gestion et la valorisation des déchets : tri, enlèvement et
              revalorisation de vos biens et déchets non dangereux. Pour vos biens en bon état, nous les
              réinsérons dans le circuit par des dons et l'équipement de médiathèques et centres
              d'apprentissage au Cameroun, contribuant ainsi au quatrième objectif du développement durable.
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}
            >
              Nous proposons le tri et la valorisation de vos déchets, l'enlèvement à domicile sur devis,
              la réinsertion des biens dans le circuit (dons, médiathèques) et un accompagnement conforme
              à la réglementation (D3E, déchets dangereux).
            </p>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Le saviez-vous : texte libre */}
      <section className="section" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Container>
          <motion.div
            ref={calloutRef}
            initial="hidden"
            animate={calloutControls}
            variants={fadeInUp}
            style={{ maxWidth: 720, margin: 0, textAlign: 'left' }}
          >
            <h4
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}
            >
              <Info size={20} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
              Le saviez-vous ?
            </h4>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}
            >
              La réglementation proscrit de mêler des déchets dangereux (seringues, piles, solvants) et
              prescrit un tri spécifique. Les déchets dangereux sont régis par l'article R541-8 du code de
              l'environnement. Un déchet est dit dangereux s'il présente une ou des propriétés de danger.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* CTA */}
      <section
        className="section"
        style={{
          background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)',
          padding: 'var(--spacing-4xl) 0',
          textAlign: 'center'
        }}
      >
        <Container>
          <motion.div
            ref={ctaRef}
            initial="hidden"
            animate={ctaControls}
            variants={fadeInUp}
          >
            <h3
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                fontWeight: 'var(--font-weight-bold)',
                color: '#fff',
                marginBottom: 'var(--spacing-md)'
              }}
            >
              Besoin d'un devis ou d'informations ?
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: 'var(--spacing-xl)'
              }}
            >
              Nous vous répondons sous 48h pour une solution adaptée à votre situation.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                variant="light"
                size="lg"
                onClick={() => (window.location.href = '/contact')}
              >
                Nous contacter
                <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default Particulier;
